// ✅ src/pages/VehicleV2/VehicleHsdFillingHrKmWholePrint.jsx
// Route: /vehicle/VehicleHsdFillingWholeRpt_Print  (as you mentioned)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehiclehsdFillingRpt1";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const getQS = (search) => {
  const p = new URLSearchParams(search);
  const obj = {};
  for (const [k, v] of p.entries()) obj[k] = v;
  return obj;
};

export default function VehicleHsdFillingHrKmWholePrint() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Accept BOTH navigate(state) and querystring
  const filters = useMemo(() => {
    const q = getQS(location.search);
    const f = location.state?.filters || {};

    return {
      branchid: toStr(f.branchid ?? f.branchId ?? q.branchid ?? q.branchId ?? "Select"),
      vehiclecode: toStr(f.vehiclecode ?? f.vehicleCode ?? q.vehiclecode ?? q.vehicleCode),
      vehiclename: toStr(f.vehiclename ?? f.vehicleName ?? q.vehiclename ?? q.vehicleName),
      regno: toStr(f.regno ?? f.vehicleRegno ?? q.regno ?? q.vehicleRegno),
      fromdate: toStr(f.fromdate ?? f.fromDate ?? q.fromdate ?? q.fromDate),
      todate: toStr(f.todate ?? f.toDate ?? q.todate ?? q.toDate),
      month: toStr(f.month ?? q.month),
      year: toStr(f.year ?? q.year),
      ownHire: toStr(f.ownHire ?? q.ownHire), // optional
      vehicleMainTypeId: toStr(f.vehicleMainTypeId ?? q.vehicleMainTypeId), // optional
    };
  }, [location.search, location.state]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState(() => (Array.isArray(location.state?.rows) ? location.state.rows : []));

  // ✅ Fixed columns for HSD Whole report (no dynamic keys, clean print)
  const columns = useMemo(
    () => [
      { key: "sno", label: "S.No", align: "center" },
      { key: "VehicleMainType", label: "Vehicle Main Type", align: "left" },
      { key: "fld_vehicle_code", label: "Vehicle Code", align: "center" },
      { key: "fld_vehicle_name", label: "Vehicle Name", align: "left" },
      { key: "fld_vehicle_regno", label: "Reg No", align: "center" },

      { key: "fld_hsdfillingWorkinhr", label: "Total Working Hr", align: "right" },
      { key: "fld_hsdfillinghr", label: "Total HSD Filling (Hr)", align: "right" },
      { key: "fld_hsdfillingWorkingKM", label: "Total Working KM", align: "right" },
      { key: "fld_hsdfillingkm", label: "Total HSD Filling (KM)", align: "right" },
      { key: "fld_diselconsumptionhr", label: "HSD Consumption Hr", align: "right" },
      { key: "fld_diselconsumptionkm", label: "HSD Consumption KM", align: "right" },
      { key: "cumulativehrs", label: "Last Updated Cummulative Hr", align: "right" },
      { key: "cumulativekm", label: "Last Updated Cummulative KM", align: "right" },
      { key: "fld_progressentrydate", label: "Last Updated Date", align: "center" },
    ],
    []
  );

  const buildPrintUrl = () => {
    const params = new URLSearchParams();

    // ✅ pass what backend expects (same naming you used in first print)
    params.set("branchid", filters.branchid);
    params.set("vehiclecode", filters.vehiclecode);
    params.set("vehiclename", filters.vehiclename);
    params.set("regno", filters.regno);
    params.set("fromdate", filters.fromdate);
    params.set("todate", filters.todate);
    params.set("month", filters.month);
    params.set("year", filters.year);

    // optional
    if (filters.ownHire) params.set("ownHire", filters.ownHire);
    if (filters.vehicleMainTypeId) params.set("vehicleMainTypeId", filters.vehicleMainTypeId);

    return `${API_BASE}/print?${params.toString()}`;
  };

  useEffect(() => {
    let alive = true;

    const load = async () => {
      // if rows already passed from previous page, skip fetch
      if (Array.isArray(location.state?.rows) && location.state.rows.length) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErr("");
        const url = buildPrintUrl();
        const { data } = await axios.get(url);

        const list = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
        const withSno = list.map((r, idx) => ({ ...r, sno: idx + 1 }));

        if (alive) setRows(withSno);
      } catch (e) {
        if (alive) {
          setRows([]);
          setErr(e?.response?.data?.message || e.message || "Print fetch failed");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerFiltersText = useMemo(() => {
    const parts = [];
    if (filters.fromdate) parts.push(`From: ${filters.fromdate}`);
    if (filters.todate) parts.push(`To: ${filters.todate}`);
    if (filters.branchid && filters.branchid !== "Select") parts.push(`Branch: ${filters.branchid}`);
    if (filters.vehiclecode) parts.push(`Code: ${filters.vehiclecode}`);
    if (filters.vehiclename) parts.push(`Name: ${filters.vehiclename}`);
    if (filters.regno) parts.push(`Reg.No: ${filters.regno}`);
    if (filters.month) parts.push(`Month: ${filters.month}`);
    if (filters.year) parts.push(`Year: ${filters.year}`);
    return parts.join(" | ");
  }, [filters]);

  const handleMail = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(`${API_BASE}/export-pdf`, {
        filters,
        rows,
      });

      const fileName = toStr(data?.fileName);
      if (!fileName) throw new Error("PDF export failed (no filename returned)");

      window.open(
        `/Mail/mail.aspx?Rpt=${encodeURIComponent(fileName)}`,
        "_blank",
        "width=650,height=280,resizable=no,scrollbars=no,toolbar=no,status=no"
      );
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Mail export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* ✅ Print CSS (same approach like your VehicleSalesorScrap print) */}
      <style>{`
        @page { size: A4 landscape; margin: 8mm; } /* landscape helps wide columns */
        @media print {
          body { margin: 0; padding: 0; font-size: 9pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; }
          th, td { border: 1px solid #000 !important; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div className="p-3">
        {/* Top actions (not printed) */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleMail}
              disabled={loading || !rows.length}
              className="px-5 py-2 border rounded hover:bg-gray-50"
              title="Export PDF and open Mail window"
            >
              📧 Mail
            </button>

            <button
              onClick={() => window.print()}
              disabled={loading}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading / Error (not printed) */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Header block */}
        <div className="border-2 border-black mb-2 avoid-break">
          <div className="flex items-center p-2">
            {/* Logo */}
            <div className="w-24 h-14 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center leading-tight">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <div className="text-lg font-bold">Nissi Infotech</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa Gounder, Kavundampalayam, Coimbatore - 641030 - Tamil Nadu.
              </div>
              <div className="text-xs">Phone No : 9003744644</div>
            </div>

            <div className="text-xs text-right w-32">
              <div>INFRA SCREEN</div>
              <div className="font-semibold">B-54</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1 avoid-break">
          <div className="text-base font-bold">VEHICLE HSD FILLING HR/KM REPORT</div>
          <div className="text-xs">{headerFiltersText || " "}</div>
        </div>

        {/* Filters block (optional, neat like ASPX) */}
        <div className="border-2 border-black mb-2 p-2 text-xs avoid-break">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <b>Branch:</b> {filters.branchid || "-"} &nbsp;&nbsp; <b>Vehicle Code:</b> {filters.vehiclecode || "-"} &nbsp;&nbsp;
              <b>Vehicle Name:</b> {filters.vehiclename || "-"}
            </div>
            <div className="text-right">
              <b>Reg No:</b> {filters.regno || "-"} &nbsp;&nbsp; <b>Month:</b> {filters.month || "-"} &nbsp;&nbsp; <b>Year:</b>{" "}
              {filters.year || "-"}
            </div>
          </div>
          <div className="mt-1">
            <b>From:</b> {filters.fromdate || "-"} &nbsp;&nbsp; <b>To:</b> {filters.todate || "-"}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-[11px]">
            <thead>
              <tr className="bg-gray-200">
                {columns.map((c) => (
                  <th key={c.key} className="px-1 py-1 font-bold text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-2 py-2 text-center">
                    No Records
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx}>
                    {columns.map((c) => {
                      const v =
                        c.key === "sno"
                          ? idx + 1
                          : r?.[c.key] === null || r?.[c.key] === undefined
                          ? ""
                          : String(r[c.key]);

                      const cls =
                        c.align === "right"
                          ? "px-1 py-1 text-right whitespace-nowrap"
                          : c.align === "center"
                          ? "px-1 py-1 text-center whitespace-nowrap"
                          : "px-1 py-1 whitespace-nowrap";

                      return (
                        <td key={c.key} className={cls}>
                          {v}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1 avoid-break">
          Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
        </div>
      </div>
    </div>
  );
}
